interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

interface CacheConfig {
  defaultTTL: number;
  maxSize: number;
  enablePersistence: boolean;
}

class CacheService {
  private cache = new Map<string, CacheItem<any>>();
  private config: CacheConfig;
  private persistenceKey = 'kiro_cache';

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = {
      defaultTTL: 5 * 60 * 1000, // 5 minutes default
      maxSize: 100,
      enablePersistence: true,
      ...config
    };

    if (this.config.enablePersistence) {
      this.loadFromStorage();
    }

    // Clean up expired items periodically
    setInterval(() => this.cleanup(), 60 * 1000); // Every minute
  }

  set<T>(key: string, data: T, ttl?: number): void {
    const item: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.config.defaultTTL
    };

    // If cache is full, remove oldest item
    if (this.cache.size >= this.config.maxSize) {
      const oldestKey = this.getOldestKey();
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, item);

    if (this.config.enablePersistence) {
      this.saveToStorage();
    }
  }

  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    
    if (!item) {
      return null;
    }

    // Check if item has expired
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      if (this.config.enablePersistence) {
        this.saveToStorage();
      }
      return null;
    }

    return item.data as T;
  }

  has(key: string): boolean {
    return this.get(key) !== null;
  }

  delete(key: string): boolean {
    const result = this.cache.delete(key);
    if (result && this.config.enablePersistence) {
      this.saveToStorage();
    }
    return result;
  }

  clear(): void {
    this.cache.clear();
    if (this.config.enablePersistence) {
      this.saveToStorage();
    }
  }

  // Get cache statistics
  getStats() {
    const now = Date.now();
    let validItems = 0;
    let expiredItems = 0;

    for (const [, item] of this.cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        expiredItems++;
      } else {
        validItems++;
      }
    }

    return {
      totalItems: this.cache.size,
      validItems,
      expiredItems,
      maxSize: this.config.maxSize,
      hitRate: this.getHitRate()
    };
  }

  // Optimistic update: set data immediately and update in background
  async setWithOptimisticUpdate<T>(
    key: string,
    optimisticData: T,
    updateFunction: () => Promise<T>,
    ttl?: number
  ): Promise<T> {
    // Set optimistic data immediately
    this.set(key, optimisticData, ttl);

    try {
      // Fetch real data in background
      const realData = await updateFunction();
      this.set(key, realData, ttl);
      return realData;
    } catch (error) {
      // If update fails, remove optimistic data
      this.delete(key);
      throw error;
    }
  }

  // Cache with stale-while-revalidate strategy
  async getWithSWR<T>(
    key: string,
    fetchFunction: () => Promise<T>,
    ttl?: number,
    staleTime?: number
  ): Promise<T> {
    const cached = this.cache.get(key);
    const now = Date.now();
    const actualStaleTime = staleTime || ttl || this.config.defaultTTL;

    // If we have fresh data, return it
    if (cached && (now - cached.timestamp) < actualStaleTime) {
      return cached.data as T;
    }

    // If we have stale data, return it but fetch fresh data in background
    if (cached && (now - cached.timestamp) < cached.ttl) {
      // Return stale data immediately
      const staleData = cached.data as T;
      
      // Fetch fresh data in background
      fetchFunction()
        .then(freshData => {
          this.set(key, freshData, ttl);
        })
        .catch(error => {
          console.warn('Background refresh failed:', error);
        });

      return staleData;
    }

    // No cached data or expired, fetch fresh data
    const freshData = await fetchFunction();
    this.set(key, freshData, ttl);
    return freshData;
  }

  private getOldestKey(): string | null {
    let oldestKey: string | null = null;
    let oldestTimestamp = Date.now();

    for (const [key, item] of this.cache.entries()) {
      if (item.timestamp < oldestTimestamp) {
        oldestTimestamp = item.timestamp;
        oldestKey = key;
      }
    }

    return oldestKey;
  }

  private cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));

    if (keysToDelete.length > 0 && this.config.enablePersistence) {
      this.saveToStorage();
    }
  }

  private hitCount = 0;
  private missCount = 0;

  private getHitRate(): number {
    const total = this.hitCount + this.missCount;
    return total > 0 ? this.hitCount / total : 0;
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.persistenceKey);
      if (stored) {
        const data = JSON.parse(stored);
        this.cache = new Map(data);
      }
    } catch (error) {
      console.warn('Failed to load cache from storage:', error);
    }
  }

  private saveToStorage(): void {
    try {
      const data = Array.from(this.cache.entries());
      localStorage.setItem(this.persistenceKey, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save cache to storage:', error);
    }
  }
}

// Create cache instances for different data types
export const workflowCache = new CacheService({
  defaultTTL: 30 * 1000, // 30 seconds for workflow status
  maxSize: 50,
  enablePersistence: false // Don't persist workflow status
});

export const projectCache = new CacheService({
  defaultTTL: 5 * 60 * 1000, // 5 minutes for project data
  maxSize: 20,
  enablePersistence: true
});

export const userCache = new CacheService({
  defaultTTL: 15 * 60 * 1000, // 15 minutes for user data
  maxSize: 10,
  enablePersistence: true
});

export default CacheService;